/**
 * "Use as completed work order" on the job page (threads/wo-in-wrong-slot).
 *
 * A signed WO uploaded as a Document or a Closeout upload never satisfied the
 * packet hold — only the Work Order slot did — and on 2026-09-30 that had 15
 * packets stuck with the signed WO sitting right there on the job. This moves
 * the file into the slot after a confirm that says exactly what will happen:
 * sends now, or still waiting on a report.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Clock } from "lucide-react";
import { confirmDialog } from "@/components/ui/app-dialogs";
import { Button } from "@/components/ui/button";
import { api, ApiError } from "@/lib/api";
import { heldPacketLine, likelySignedWorkOrder, type PacketStatusLike } from "@/lib/signed-work-order";
import { formatDate } from "@/lib/utils";
import type { LinkedInvoice } from "./types";

export interface PacketStatus extends PacketStatusLike {
    reports: { filed: number; total: number; missing?: Array<{ serialNumber: string | null; name: string }> };
    wo_applicable?: boolean;
    recipients?: string[];
}

type Release = "released" | "not_parked" | "waiting" | "failed";

export function usePacketStatus(jobId: string, enabled = true) {
    return useQuery({
        queryKey: ["job", jobId, "packet-status"],
        queryFn: () => api.get<PacketStatus>(`/api/office-review/jobs/${jobId}/packet-status`),
        enabled,
        staleTime: 30_000,
    });
}

interface MovableFile {
    id: string;
    file_name: string;
    created_at: string;
    uploaded_by_name?: string | null;
}

function money(n: number | string | null | undefined): string {
    const v = Number(n ?? 0);
    return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** The confirm text — what moves, and whether an email goes out because of it. */
export function confirmMessage(
    file: { file_name: string },
    packet: PacketStatus | null | undefined,
    invoices: Array<Pick<LinkedInvoice, "invoice_number" | "total_amount" | "status">>,
): { message: string; confirmLabel: string; sendsNow: boolean } {
    const head = `${file.file_name} moves into the Work Order slot. It's the work order the customer gets.`;
    if (!packet?.held) {
        return { message: head, confirmLabel: "Use as completed WO", sendsNow: false };
    }
    if (packet.waiting_on_reports) {
        const missing = (packet.reports.missing ?? [])
            .map((m) => (m.serialNumber ? `S/N ${m.serialNumber}` : m.name))
            .join(", ");
        const still = `${packet.reports.total - packet.reports.filed} official report${
            packet.reports.total - packet.reports.filed === 1 ? "" : "s"
        }${missing ? ` (${missing})` : ""}`;
        return {
            message: `${head}\n\nThe packet is still waiting on ${still}, so nothing sends now. It goes out on its own when that lands.`,
            confirmLabel: "Use as completed WO",
            sendsNow: false,
        };
    }
    const to = packet.recipients ?? [];
    const inv = invoices
        .filter((i) => i.status !== "void" && i.status !== "cancelled")
        .map((i) => `Invoice #${i.invoice_number} · ${money(i.total_amount)}`)
        .join(", ");
    const reports =
        packet.reports.total > 0 ? `${packet.reports.total} report${packet.reports.total === 1 ? "" : "s"}` : "";
    const carried = [inv, reports, "work order"].filter(Boolean).join(" · ");
    const who =
        to.length === 0
            ? "the packet's recipients"
            : `${to.length} address${to.length === 1 ? "" : "es"}: ${to.join(", ")}`;
    return {
        message: `${head}\n\nThe packet was waiting only on this. It sends as soon as you confirm, to ${who}.\n\nIn the email: ${carried}.`,
        confirmLabel: "Use it and send the packet",
        sendsNow: true,
    };
}

export function useUseAsWorkOrder({
    jobId,
    packet,
    invoices,
}: {
    jobId: string;
    packet: PacketStatus | null | undefined;
    invoices: LinkedInvoice[];
}) {
    const qc = useQueryClient();
    const refresh = () => {
        qc.invalidateQueries({ queryKey: ["job", jobId] });
        qc.invalidateQueries({ queryKey: ["send-history", jobId] });
    };

    const undo = useMutation({
        mutationFn: (attachmentId: string) =>
            api.post<{ restoredCategory: string }>(`/api/attachments/${attachmentId}/undo-use-as-work-order`, {}),
        onSuccess: () => {
            refresh();
            toast.success("Moved back. An email that already went out can't be unsent.");
        },
        onError: (err) => {
            const info = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
            toast.error(info?.message ?? "Couldn't move it back.");
        },
    });

    const move = useMutation({
        mutationFn: (attachmentId: string) =>
            api.post<{ release: Release; fileName: string }>(`/api/attachments/${attachmentId}/use-as-work-order`, {}),
        onSuccess: (res, attachmentId) => {
            refresh();
            const undoAction = { label: "Undo move", onClick: () => undo.mutate(attachmentId) };
            if (res.release === "released") {
                toast.success("Packet sent — invoice, reports and work order.", { action: undoAction });
            } else if (res.release === "failed") {
                // The move stands; the send is reported on its own and never as "sent".
                toast.error("The work order is in, but the packet didn't reach anyone. It's still held — resend it.", {
                    duration: 12_000,
                });
            } else if (res.release === "waiting") {
                toast.success("Moved to the Work Order slot. The packet is still waiting on a report.", {
                    action: undoAction,
                });
            } else {
                toast.success("Moved to the Work Order slot.", { action: undoAction });
            }
        },
        onError: (err) => {
            const info = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
            toast.error(info?.message ?? "Couldn't use that file as the work order.");
        },
    });

    const start = async (file: MovableFile) => {
        const c = confirmMessage(file, packet, invoices);
        const ok = await confirmDialog({
            title: "Use this as the completed work order?",
            message: c.message,
            confirmLabel: c.confirmLabel,
        });
        if (ok) move.mutate(file.id);
    };

    return { start, pending: move.isPending || undo.isPending };
}

/**
 * The held-packet line above the file list. Names the real blocker, and on a
 * job waiting only on its WO, points at the file that looks like it.
 */
export function HeldPacketBanner({
    packet,
    candidates,
    woNumber,
    onUse,
    pending,
    onSentAnotherWay,
}: {
    packet: PacketStatus | null | undefined;
    candidates: MovableFile[];
    woNumber: string | null;
    onUse: (file: MovableFile) => void;
    pending: boolean;
    /** "Already sent another way" (2026-10-07). Omitted = not offered. */
    onSentAnotherWay?: () => void;
}) {
    const line = heldPacketLine(packet);
    if (!line) return null;
    const woOnly = !!packet?.waiting_on_work_order;
    const suggestion = woOnly ? likelySignedWorkOrder(candidates, woNumber) : null;
    const tone = woOnly
        ? "border-orange-300/70 bg-orange-50 text-orange-900 dark:border-orange-500/40 dark:bg-orange-950/40 dark:text-orange-200"
        : "border-amber-300/60 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200";
    return (
        <div
            data-testid="held-packet-banner"
            className={`flex flex-wrap items-start justify-between gap-2 rounded-md border px-3 py-2 text-xs ${tone}`}
        >
            <div className="flex min-w-0 items-start gap-2">
                <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                    <span className="font-semibold">{line}</span>
                    {woOnly && suggestion && (
                        <>
                            <br />
                            This file looks like it: <span className="font-semibold">{suggestion.file_name}</span>
                            {suggestion.uploaded_by_name ? ` (${suggestion.uploaded_by_name}` : " ("}
                            {suggestion.uploaded_by_name ? " · " : ""}
                            {formatDate(suggestion.created_at)}).
                        </>
                    )}
                    {woOnly && !suggestion && (
                        <>
                            <br />
                            No signed work order is on this job yet — upload it in Closeout&apos;s Work Order slot.
                        </>
                    )}
                </span>
            </div>
            {woOnly && suggestion && (
                <Button
                    size="sm"
                    className="h-7 shrink-0 bg-orange-700 text-xs text-white hover:bg-orange-800"
                    disabled={pending}
                    onClick={() => onUse(suggestion)}
                >
                    Use as completed WO
                </Button>
            )}
            {onSentAnotherWay && (
                <Button
                    size="sm"
                    variant={packet?.no_customer_email ? "default" : "outline"}
                    className="h-7 shrink-0 text-xs"
                    onClick={onSentAnotherWay}
                    data-testid="held-sent-another-way"
                >
                    Already sent another way
                </Button>
            )}
        </div>
    );
}
